import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import HeaderWithSearch from "./HeaderWithSearch";
import JSZip from "jszip";
import "./SalesDashboard.css";
import { auth, db } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  updateProfile,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  writeBatch,
  getDocs,
  deleteField,
} from "firebase/firestore";

/* ======================== HELPERS ======================== */
function formatCurrency(amount) {
  return "$" + Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** YYYY-MM-DD in the user's local calendar (same as the sale date field). */
function localDateString(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const parts = String(dateStr).split("-");
  if (parts.length === 3) {
    const y = Number(parts[0]);
    const mo = Number(parts[1]);
    const da = Number(parts[2]);
    if (Number.isFinite(y) && Number.isFinite(mo) && Number.isFinite(da)) {
      return new Date(y, mo - 1, da).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function csvEscapeCell(value) {
  if (value == null || value === "") return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, csvText) {
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function buildSalesCsv(salesRows) {
  const headers = [
    "id",
    "date",
    "productName",
    "productPrice",
    "qty",
    "total",
    "customer",
    "occasion",
    "note",
    "countsTowardGoal",
    "createdAt",
  ];
  const lines = [headers.join(",")];
  const sorted = [...salesRows].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
  for (const s of sorted) {
    lines.push(headers.map((h) => csvEscapeCell(s[h])).join(","));
  }
  return lines.join("\r\n");
}

function buildProductsCsv(productsRows) {
  const headers = ["id", "name", "price", "category", "stock", "createdAt"];
  const lines = [headers.join(",")];
  for (const p of productsRows) {
    lines.push(headers.map((h) => csvEscapeCell(p[h])).join(","));
  }
  return lines.join("\r\n");
}

function buildCustomersCsv(customersRows) {
  const headers = ["id", "name", "email", "phone", "createdAt"];
  const lines = [headers.join(",")];
  for (const c of customersRows) {
    lines.push(headers.map((h) => csvEscapeCell(c[h])).join(","));
  }
  return lines.join("\r\n");
}


/* ======================== MINI BAR CHART (dark theme) ======================== */
function BarChart({ data, height = 120 }) {
  if (!data || data.length === 0) return <p className="sd-empty">No data yet.</p>;
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <span className="sd-bar-label">{formatCurrency(d.value)}</span>
          <div
            style={{
              width: "100%",
              height: Math.max(6, (d.value / max) * (height - 30)),
              background: "linear-gradient(180deg, rgba(0,240,255,0.8), rgba(0,120,240,0.6))",
              borderRadius: "6px 6px 0 0",
              transition: "height 0.4s",
              boxShadow: d.value > 0 ? "0 0 10px rgba(0,240,255,0.3)" : "none",
            }}
          />
          <span className="sd-bar-axis">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ======================== STAT CARD (dark theme) ======================== */
function StatCard({ icon, title, value, borderClass }) {
  return (
    <div className={`sd-stat-card sd-glass-card ${borderClass}`}>
      <div className="sd-stat-icon-wrap">{icon}</div>
      <div>
        <div className="sd-stat-title">{title}</div>
        <div className="sd-stat-value">{value}</div>
      </div>
    </div>
  );
}

/* ======================== PARTICLE CANVAS ======================== */
function useParticles(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const ctx = canvas.getContext("2d");
    let particles = [];
    const narrow = window.innerWidth < 768;
    const COUNT = narrow ? 20 : 36;
    const linkDist = narrow ? 0 : 110;
    let animId;

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    class P {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.sx = (Math.random() - 0.5) * 0.4;
        this.sy = (Math.random() - 0.5) * 0.4;
        this.opacity = Math.random() * 0.4 + 0.1;
        const colors = ["0,240,255", "168,85,247", "0,255,136", "255,45,149"];
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }
      update() {
        this.x += this.sx; this.y += this.sy;
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) this.reset();
      }
      draw() {
        ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.color},${this.opacity})`; ctx.fill();
      }
    }

    for (let i = 0; i < COUNT; i++) particles.push(new P());

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => { p.update(); p.draw(); });
      if (linkDist > 0) {
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < linkDist) {
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.strokeStyle = `rgba(0,240,255,${0.05 * (1 - dist / linkDist)})`;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }
      }
      animId = requestAnimationFrame(animate);
    }
    animate();
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animId);
    };
  }, [canvasRef]);
}

/* ======================== MAIN COMPONENT ======================== */
export default function SalesDashboard() {
  const canvasRef = useRef(null);
  useParticles(canvasRef);

  /* --- Auth state --- */
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState("login");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupBiz, setSignupBiz] = useState("");
  const [authError, setAuthError] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [backupBusy, setBackupBusy] = useState(false);

  /* --- App state (from Firestore) --- */
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [firestoreError, setFirestoreError] = useState("");
  const [tab, setTab] = useState("dashboard");

  // Product form
  const [prodName, setProdName] = useState("");
  const [prodPrice, setProdPrice] = useState("");
  const [prodCategory, setProdCategory] = useState("General");
  const [prodStock, setProdStock] = useState("");
  const [prodEditId, setProdEditId] = useState(null);
  const [prodMsg, setProdMsg] = useState("");

  // Sale form
  const [saleProductId, setSaleProductId] = useState("");
  const [saleQty, setSaleQty] = useState("");
  const [saleCustomer, setSaleCustomer] = useState("");
  const [saleDate, setSaleDate] = useState(localDateString());
  const [saleNote, setSaleNote] = useState("");
  const [saleOccasion, setSaleOccasion] = useState("");
  const [saleCountsTowardGoal, setSaleCountsTowardGoal] = useState(true);
  const [saleMsg, setSaleMsg] = useState("");

  const [goalFormProductId, setGoalFormProductId] = useState("");
  const [goalFormQty, setGoalFormQty] = useState("");
  const [goalFormLabel, setGoalFormLabel] = useState("");
  const [goalSaveMsg, setGoalSaveMsg] = useState("");
  const [showGoalCelebration, setShowGoalCelebration] = useState(false);
  const prevSoldForCelebrationRef = useRef(null);
  const goalConfigKeyRef = useRef("");

  // Customer form
  const [custName, setCustName] = useState("");
  const [custEmail, setCustEmail] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [custMsg, setCustMsg] = useState("");

  // Filters
  const [salesSearch, setSalesSearch] = useState("");
  const [salesDateFrom, setSalesDateFrom] = useState("");
  const [salesDateTo, setSalesDateTo] = useState("");

  /* --- Load fonts --- */
  useEffect(() => {
    if (!document.querySelector('link[href*="Orbitron"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap";
      document.head.appendChild(link);
    }
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const fa = document.createElement("link");
      fa.rel = "stylesheet";
      fa.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css";
      document.head.appendChild(fa);
    }
  }, []);

  /* --- Firebase Auth listener --- */
  useEffect(() => {
    let profileUnsub = null;
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      // Clean up previous profile listener
      if (profileUnsub) { profileUnsub(); profileUnsub = null; }
      if (firebaseUser) {
        setCurrentUser(firebaseUser);
        // Load user profile from Firestore
        profileUnsub = onSnapshot(
          doc(db, "users", firebaseUser.uid),
          (snap) => {
            if (snap.exists()) {
              setUserProfile(snap.data());
            } else {
              setUserProfile({ name: firebaseUser.displayName || "User", businessName: "My Business" });
            }
          },
          (err) => {
            console.error("Profile listener error:", err);
            setFirestoreError("Profile: " + err.message);
          }
        );
        setAuthLoading(false);
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setAuthLoading(false);
      }
    });
    return () => {
      if (profileUnsub) profileUnsub();
      unsub();
    };
  }, []);

  /* --- Firestore real-time listeners (when user is logged in) --- */
  useEffect(() => {
    if (!currentUser) {
      setProducts([]);
      setSales([]);
      setCustomers([]);
      return;
    }
    const uid = currentUser.uid;

    setFirestoreError("");
    const unsubProducts = onSnapshot(
      query(collection(db, "users", uid, "products"), orderBy("createdAt", "desc")),
      (snap) => { setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
      (err) => { console.error("Products listener error:", err); setFirestoreError("Products: " + err.message); }
    );
    const unsubSales = onSnapshot(
      query(collection(db, "users", uid, "sales"), orderBy("createdAt", "desc")),
      (snap) => { setSales(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
      (err) => { console.error("Sales listener error:", err); setFirestoreError("Sales: " + err.message); }
    );
    const unsubCustomers = onSnapshot(
      query(collection(db, "users", uid, "customers"), orderBy("createdAt", "desc")),
      (snap) => { setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
      (err) => { console.error("Customers listener error:", err); setFirestoreError("Customers: " + err.message); }
    );

    return () => { unsubProducts(); unsubSales(); unsubCustomers(); };
  }, [currentUser]);

  useEffect(() => {
    if (!userProfile) return;
    setGoalFormProductId(userProfile.salesGoalProductId || "");
    setGoalFormQty(
      userProfile.salesGoalQty != null && Number(userProfile.salesGoalQty) > 0
        ? String(userProfile.salesGoalQty)
        : ""
    );
    setGoalFormLabel(userProfile.salesGoalTitle || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when goal fields / uid change, not whole profile
  }, [
    currentUser?.uid,
    userProfile?.salesGoalProductId,
    userProfile?.salesGoalQty,
    userProfile?.salesGoalTitle,
  ]);

  useEffect(() => {
    const pid = userProfile?.salesGoalProductId || "";
    const q = Number(userProfile?.salesGoalQty) || 0;
    const key = `${pid}|${q}`;
    if (key !== goalConfigKeyRef.current) {
      goalConfigKeyRef.current = key;
      prevSoldForCelebrationRef.current = null;
    }
  }, [userProfile?.salesGoalProductId, userProfile?.salesGoalQty]);

  /* Clear Record Sale draft when leaving that tab — avoids stale product/qty pairing with goal saves. */
  useEffect(() => {
    if (tab !== "record-sale") {
      setSaleProductId("");
      setSaleQty("");
      setSaleMsg("");
    }
  }, [tab]);

  useEffect(() => {
    const pid = userProfile?.salesGoalProductId || "";
    const target = Number(userProfile?.salesGoalQty) || 0;
    if (!pid || target <= 0) return;

    const sold = sales
      .filter(
        (s) =>
          s.productId === pid && s.countsTowardGoal !== false
      )
      .reduce((sum, s) => sum + (Number(s.qty) || 0), 0);

    if (prevSoldForCelebrationRef.current === null) {
      prevSoldForCelebrationRef.current = sold;
      return;
    }
    const prev = prevSoldForCelebrationRef.current;
    const wasBelow = prev < target;
    const nowMet = sold >= target;
    if (wasBelow && nowMet) {
      setShowGoalCelebration(true);
      const t = setTimeout(() => setShowGoalCelebration(false), 5000);
      prevSoldForCelebrationRef.current = sold;
      return () => clearTimeout(t);
    }
    prevSoldForCelebrationRef.current = sold;
  }, [sales, userProfile?.salesGoalProductId, userProfile?.salesGoalQty]);

  /* --- Click outside user menu --- */
  useEffect(() => {
    function handleClick(e) {
      if (e.target.closest(".sd-delete-modal")) return;
      if (!e.target.closest(".sd-user-avatar") && !e.target.closest(".sd-user-menu")) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  /* ==================== AUTH HANDLERS (Firebase Auth) ==================== */
  async function handleLogin() {
    if (!loginEmail || !loginPassword) { setAuthError("Please fill in all fields."); return; }
    try {
      setAuthError("");
      await signInWithEmailAndPassword(auth, loginEmail.trim(), loginPassword);
      setLoginEmail(""); setLoginPassword("");
    } catch (err) {
      const msg = err.code === "auth/invalid-credential" ? "Invalid email or password."
        : err.code === "auth/user-not-found" ? "No account found with this email."
        : err.code === "auth/wrong-password" ? "Incorrect password."
        : err.code === "auth/too-many-requests" ? "Too many attempts. Try again later."
        : err.message;
      setAuthError(msg);
    }
  }

  async function handleSignup() {
    const name = signupName.trim();
    const email = signupEmail.trim().toLowerCase();
    const password = signupPassword;
    const biz = signupBiz.trim();
    if (!name || !email || !password) { setAuthError("Please fill in all fields."); return; }
    if (password.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    try {
      setAuthError("");
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });
      // Save user profile to Firestore
      await setDoc(doc(db, "users", cred.user.uid), {
        name,
        email,
        businessName: biz || name + "'s Business",
        createdAt: new Date().toISOString(),
      });
      setSignupName(""); setSignupEmail(""); setSignupPassword(""); setSignupBiz("");
    } catch (err) {
      const msg = err.code === "auth/email-already-in-use" ? "Email already registered."
        : err.code === "auth/weak-password" ? "Password must be at least 6 characters."
        : err.code === "auth/invalid-email" ? "Invalid email address."
        : err.message;
      setAuthError(msg);
    }
  }

  async function handleLogout() {
    await signOut(auth);
    setShowUserMenu(false);
    setTab("dashboard");
    setAuthMode("login");
  }

  function handleExportAllSalesCsv() {
    const stamp = localDateString();
    downloadCsv(`sales-all-${stamp}.csv`, buildSalesCsv(userSales));
    setShowUserMenu(false);
  }

  function handleExportFilteredSalesCsv() {
    const stamp = localDateString();
    downloadCsv(`sales-filtered-${stamp}.csv`, buildSalesCsv(filteredSales));
  }

  function handleExportProductsCsv() {
    const stamp = localDateString();
    downloadCsv(`products-${stamp}.csv`, buildProductsCsv(userProducts));
    setShowUserMenu(false);
  }

  function handleExportCustomersCsv() {
    const stamp = localDateString();
    downloadCsv(`customers-${stamp}.csv`, buildCustomersCsv(userCustomers));
    setShowUserMenu(false);
  }
  async function handleExportBackupZip() {
    if (backupBusy) return;
    setBackupBusy(true);
    try {
      const stamp = localDateString();
      const zip = new JSZip();
      const profile = {
        uid: currentUser?.uid || "",
        email: currentUser?.email || "",
        name: userProfile?.name || currentUser?.displayName || "",
        businessName: userProfile?.businessName || "",
        salesGoalTitle: userProfile?.salesGoalTitle || "",
        salesGoalProductId: userProfile?.salesGoalProductId || "",
        salesGoalQty: Number(userProfile?.salesGoalQty) || 0,
        exportedAt: new Date().toISOString(),
      };
      zip.file("profile.json", JSON.stringify(profile, null, 2));
      zip.file("sales.csv", buildSalesCsv(userSales));
      zip.file("products.csv", buildProductsCsv(userProducts));
      zip.file("customers.csv", buildCustomersCsv(userCustomers));
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales-dashboard-backup-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setShowUserMenu(false);
    } catch (err) {
      alert("Could not create backup zip: " + err.message);
    } finally {
      setBackupBusy(false);
    }
  }


  async function deleteAllDocsInSubcollection(uid, subName) {
    const snap = await getDocs(collection(db, "users", uid, subName));
    const docs = snap.docs;
    for (let i = 0; i < docs.length; i += 450) {
      const batch = writeBatch(db);
      docs.slice(i, i + 450).forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
  }

  async function handleDeleteAccount() {
    setDeleteError("");
    if (!currentUser?.email) {
      setDeleteError("This account has no email on file.");
      return;
    }
    if (deleteConfirmText.trim() !== "DELETE") {
      setDeleteError('Type the word DELETE (all caps) to confirm.');
      return;
    }
    if (!deletePassword) {
      setDeleteError("Enter your password.");
      return;
    }
    setDeleteBusy(true);
    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        deletePassword
      );
      await reauthenticateWithCredential(currentUser, credential);
    } catch (err) {
      setDeleteBusy(false);
      const msg =
        err.code === "auth/wrong-password" || err.code === "auth/invalid-credential"
          ? "Wrong password."
          : err.code === "auth/too-many-requests"
            ? "Too many attempts. Try again later."
            : err.message;
      setDeleteError(msg);
      return;
    }
    const uid = currentUser.uid;
    try {
      await deleteAllDocsInSubcollection(uid, "products");
      await deleteAllDocsInSubcollection(uid, "sales");
      await deleteAllDocsInSubcollection(uid, "customers");
      await deleteDoc(doc(db, "users", uid));
    } catch (err) {
      setDeleteBusy(false);
      setDeleteError("Could not remove all data: " + err.message);
      return;
    }
    try {
      await deleteUser(currentUser);
      setShowDeleteModal(false);
      setDeletePassword("");
      setDeleteConfirmText("");
      setDeleteBusy(false);
    } catch (err) {
      setDeleteError(
        "Account data was removed but sign-in could not be deleted: " +
          err.message +
          " Try signing in again or contact support."
      );
      setDeleteBusy(false);
    }
  }

  async function handleSaveSalesGoal(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    if (!currentUser) return;
    const qtyRaw = goalFormQty.trim();
    const qtyNum = qtyRaw === "" ? 0 : parseInt(qtyRaw, 10);
    if (qtyRaw !== "" && (Number.isNaN(qtyNum) || qtyNum < 1)) {
      setGoalSaveMsg("Enter a whole number of units (1 or more), or clear to remove the goal.");
      setTimeout(() => setGoalSaveMsg(""), 4000);
      return;
    }
    const hasGoal = goalFormProductId && qtyNum >= 1;
    try {
      setGoalSaveMsg("");
      const base = {
        name: userProfile?.name || currentUser.displayName || "User",
        email: currentUser.email || "",
        businessName: userProfile?.businessName || "My Business",
        updatedAt: new Date().toISOString(),
      };
      if (hasGoal) {
        await setDoc(
          doc(db, "users", currentUser.uid),
          {
            ...base,
            salesGoalProductId: goalFormProductId,
            salesGoalQty: qtyNum,
            salesGoalTitle: goalFormLabel.trim(),
            salesGoalTarget: deleteField(),
          },
          { merge: true }
        );
      } else {
        await setDoc(
          doc(db, "users", currentUser.uid),
          {
            ...base,
            salesGoalProductId: deleteField(),
            salesGoalQty: deleteField(),
            salesGoalTitle: deleteField(),
            salesGoalTarget: deleteField(),
          },
          { merge: true }
        );
      }
      setGoalSaveMsg(hasGoal ? "Goal saved!" : "Goal cleared.");
      setTimeout(() => setGoalSaveMsg(""), 3000);
      /* Never treat goal target as a sale — clear any leftover Record Sale product/qty from memory. */
      setSaleProductId("");
      setSaleQty("");
      setSaleMsg("");
    } catch (err) {
      setGoalSaveMsg("Error: " + err.message);
      setTimeout(() => setGoalSaveMsg(""), 5000);
    }
  }

  /* ==================== DATA — from Firestore (already loaded via listeners) ==================== */
  const userProducts = products;
  const userSales = sales;
  const userCustomers = customers;

  /* ---- Products (Firestore) ---- */
  async function handleAddProduct(e) {
    e.preventDefault();
    if (!prodName.trim() || !prodPrice) { setProdMsg("Please fill in product name and price."); return; }
    const price = parseFloat(prodPrice);
    if (isNaN(price) || price < 0) { setProdMsg("Invalid price."); return; }
    const stock = parseInt(prodStock) || 0;
    const uid = currentUser.uid;
    try {
      if (prodEditId) {
        await updateDoc(doc(db, "users", uid, "products", prodEditId), {
          name: prodName.trim(), price, category: prodCategory, stock,
        });
        setProdMsg("Product updated!");
        setProdEditId(null);
      } else {
        await addDoc(collection(db, "users", uid, "products"), {
          name: prodName.trim(), price, category: prodCategory, stock,
          createdAt: new Date().toISOString(),
        });
        setProdMsg("Product added!");
      }
      setProdName(""); setProdPrice(""); setProdCategory("General"); setProdStock("");
      setTimeout(() => setProdMsg(""), 3000);
    } catch (err) {
      setProdMsg("Error: " + err.message);
    }
  }

  function handleEditProduct(p) {
    setProdEditId(p.id);
    setProdName(p.name);
    setProdPrice(p.price);
    setProdCategory(p.category || "General");
    setProdStock(p.stock || 0);
  }

  async function handleDeleteProduct(id) {
    if (!window.confirm("Delete this product?")) return;
    await deleteDoc(doc(db, "users", currentUser.uid, "products", id));
  }

  /* ---- Sales (Firestore) ---- */
  async function handleAddSale(e) {
    e.preventDefault();
    if (tab !== "record-sale") return;
    if (!saleProductId) { setSaleMsg("Please select a product."); return; }
    const qty = parseInt(saleQty);
    if (!qty || qty <= 0) { setSaleMsg("Please enter a valid quantity."); return; }
    const product = userProducts.find(p => p.id === saleProductId);
    if (!product) { setSaleMsg("Product not found."); return; }
    const availableStock = product.stock || 0;
    if (qty > availableStock) {
      setSaleMsg(`Not enough stock! Only ${availableStock} unit(s) available.`);
      return;
    }
    const total = product.price * qty;
    const uid = currentUser.uid;
    try {
      // Add sale record
      await addDoc(collection(db, "users", uid, "sales"), {
        productId: saleProductId, productName: product.name,
        productPrice: product.price, qty, total,
        customer: saleCustomer.trim() || "Walk-in",
        date: saleDate, note: saleNote.trim(),
        occasion: saleOccasion.trim(),
        countsTowardGoal: saleCountsTowardGoal,
        createdAt: new Date().toISOString(),
      });
      // Update stock
      await updateDoc(doc(db, "users", uid, "products", saleProductId), {
        stock: Math.max(0, (product.stock || 0) - qty),
      });
      // Auto-add customer if new
      if (saleCustomer.trim() && !userCustomers.some(c => c.name.toLowerCase() === saleCustomer.trim().toLowerCase())) {
        await addDoc(collection(db, "users", uid, "customers"), {
          name: saleCustomer.trim(), email: "", phone: "",
          createdAt: new Date().toISOString(),
        });
      }
      setSaleMsg(`Sale recorded! Total: ${formatCurrency(total)}`);
      setSaleProductId(""); setSaleQty(""); setSaleCustomer(""); setSaleNote("");
      setSaleOccasion("");
      setSaleCountsTowardGoal(true);
      setSaleDate(localDateString());
      setTimeout(() => setSaleMsg(""), 4000);
    } catch (err) {
      setSaleMsg("Error: " + err.message);
    }
  }

  async function handleDeleteSale(id) {
    if (!window.confirm("Delete this sale? Stock for that product will be restored.")) return;
    const sale = userSales.find(s => s.id === id);
    const uid = currentUser.uid;
    if (!sale) {
      try {
        await deleteDoc(doc(db, "users", uid, "sales", id));
      } catch (err) {
        alert("Could not delete sale: " + err.message);
      }
      return;
    }
    const qty = Number(sale.qty) || 0;
    const batch = writeBatch(db);
    batch.delete(doc(db, "users", uid, "sales", id));
    const product = userProducts.find(p => p.id === sale.productId);
    if (product && sale.productId && qty > 0) {
      batch.update(doc(db, "users", uid, "products", sale.productId), {
        stock: (product.stock || 0) + qty,
      });
    }
    try {
      await batch.commit();
    } catch (err) {
      alert("Could not delete sale: " + err.message);
    }
  }

  /* ---- Customers (Firestore) ---- */
  async function handleAddCustomer(e) {
    e.preventDefault();
    if (!custName.trim()) { setCustMsg("Please enter customer name."); return; }
    if (userCustomers.some(c => c.name.toLowerCase() === custName.trim().toLowerCase())) {
      setCustMsg("Customer already exists."); return;
    }
    try {
      await addDoc(collection(db, "users", currentUser.uid, "customers"), {
        name: custName.trim(), email: custEmail.trim(), phone: custPhone.trim(),
        createdAt: new Date().toISOString(),
      });
      setCustMsg("Customer added!");
      setCustName(""); setCustEmail(""); setCustPhone("");
      setTimeout(() => setCustMsg(""), 3000);
    } catch (err) {
      setCustMsg("Error: " + err.message);
    }
  }

  async function handleDeleteCustomer(id) {
    if (!window.confirm("Delete this customer?")) return;
    await deleteDoc(doc(db, "users", currentUser.uid, "customers", id));
  }

  /* ---- Analytics (user-scoped) ---- */
  const totalRevenue = userSales.reduce((sum, s) => sum + s.total, 0);
  const goalProductId = userProfile?.salesGoalProductId || "";
  const goalQtyTarget = Number(userProfile?.salesGoalQty) || 0;
  const goalProduct = goalProductId
    ? userProducts.find((p) => p.id === goalProductId)
    : null;
  const soldUnitsTowardGoal =
    goalProductId && goalQtyTarget > 0
      ? userSales
          .filter(
            (s) =>
              s.productId === goalProductId &&
              s.countsTowardGoal !== false
          )
          .reduce((sum, s) => sum + (Number(s.qty) || 0), 0)
      : 0;
  const goalLabelDisplay = (userProfile?.salesGoalTitle || "").trim();
  const goalProgressPct =
    goalQtyTarget > 0 && goalProductId
      ? Math.min(100, (soldUnitsTowardGoal / goalQtyTarget) * 100)
      : 0;
  const totalSalesCount = userSales.length;
  const totalProductsCount = userProducts.length;
  const totalCustomersCount = userCustomers.length;

  const revenueByProduct = userProducts.map(p => ({
    label: p.name.length > 10 ? p.name.slice(0, 9) + "\u2026" : p.name,
    value: userSales.filter(s => s.productId === p.id).reduce((sum, s) => sum + s.total, 0),
  })).sort((a, b) => b.value - a.value).slice(0, 6);

  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (6 - i));
    const key = localDateString(d);
    return {
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      value: userSales.filter(s => s.date === key).reduce((sum, s) => sum + s.total, 0),
    };
  });

  const topCustomers = userCustomers.map(c => ({
    name: c.name,
    totalSpent: userSales.filter(s => s.customer.toLowerCase() === c.name.toLowerCase()).reduce((sum, s) => sum + s.total, 0),
    salesCount: userSales.filter(s => s.customer.toLowerCase() === c.name.toLowerCase()).length,
  })).sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 5);

  const lowStock = userProducts.filter(p => (p.stock || 0) <= 5);

  const filteredSales = userSales.filter(s => {
    const q = salesSearch.toLowerCase();
    const matchSearch =
      !q ||
      (s.productName || "").toLowerCase().includes(q) ||
      (s.customer || "").toLowerCase().includes(q) ||
      (s.occasion || "").toLowerCase().includes(q);
    const matchFrom = !salesDateFrom || s.date >= salesDateFrom;
    const matchTo = !salesDateTo || s.date <= salesDateTo;
    return matchSearch && matchFrom && matchTo;
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const tabs = [
    { key: "dashboard", icon: "fa-chart-line", label: "Overview" },
    { key: "record-sale", icon: "fa-cash-register", label: "Record Sale" },
    { key: "products", icon: "fa-boxes-stacked", label: "Products" },
    { key: "sales-history", icon: "fa-receipt", label: "Sales History" },
    { key: "customers", icon: "fa-users", label: "Customers" },
  ];

  /* ======================== RENDER ======================== */
  return (
    <div className="salesdash-app">
      <HeaderWithSearch />
      <canvas ref={canvasRef} className="sd-particle-canvas" />

      {/* ========== LOADING ========== */}
      {authLoading && (
        <div className="sd-screen sd-active">
          <div className="sd-auth-container">
            <div className="sd-logo-icon" style={{ animation: "pulse 1.5s infinite" }}><i className="fas fa-chart-line"></i></div>
            <p style={{ color: "rgba(255,255,255,0.4)", marginTop: 16 }}>Loading...</p>
          </div>
        </div>
      )}

      {/* ========== AUTH SCREEN ========== */}
      {!authLoading && !currentUser && (
        <div className="sd-screen sd-active">
          <div className="sd-auth-container">
            <div className="sd-logo-section">
              <div className="sd-logo-icon"><i className="fas fa-chart-line"></i></div>
              <h1 className="sd-logo-text">Sales<span className="sd-accent">Dashboard</span></h1>
              <p className="sd-tagline">The smart way to track sales &amp; grow your business</p>
            </div>

            {authMode === "login" ? (
              <div className="sd-auth-form sd-glass-card">
                <h2><i className="fas fa-fingerprint"></i> Sign In</h2>
                <div className="sd-input-group">
                  <i className="fas fa-envelope sd-input-icon"></i>
                  <input type="email" placeholder="Email address" value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleLogin()} />
                </div>
                <div className="sd-input-group">
                  <i className="fas fa-lock sd-input-icon"></i>
                  <input type="password" placeholder="Password" value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleLogin()} />
                </div>
                {authError && <div className="sd-auth-error"><i className="fas fa-exclamation-circle"></i> {authError}</div>}
                <button className="sd-btn sd-btn-primary sd-btn-glow" onClick={handleLogin} style={{ width: "100%" }}>
                  <span>Access Dashboard</span><i className="fas fa-arrow-right"></i>
                </button>
                <p className="sd-switch-auth">Don't have an account?{" "}
                  <a href="#!" onClick={e => { e.preventDefault(); setAuthMode("signup"); setAuthError(""); }}>Create Account</a>
                </p>
              </div>
            ) : (
              <div className="sd-auth-form sd-glass-card">
                <h2><i className="fas fa-user-plus"></i> Create Account</h2>
                <div className="sd-input-group">
                  <i className="fas fa-user sd-input-icon"></i>
                  <input type="text" placeholder="Full name" value={signupName} onChange={e => setSignupName(e.target.value)} />
                </div>
                <div className="sd-input-group">
                  <i className="fas fa-envelope sd-input-icon"></i>
                  <input type="email" placeholder="Email address" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} />
                </div>
                <div className="sd-input-group">
                  <i className="fas fa-lock sd-input-icon"></i>
                  <input type="password" placeholder="Password (6+ characters)" value={signupPassword} onChange={e => setSignupPassword(e.target.value)} />
                </div>
                <div className="sd-input-group">
                  <i className="fas fa-building sd-input-icon"></i>
                  <input type="text" placeholder="Business name (optional)" value={signupBiz} onChange={e => setSignupBiz(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSignup()} />
                </div>
                {authError && <div className="sd-auth-error"><i className="fas fa-exclamation-circle"></i> {authError}</div>}
                <button className="sd-btn sd-btn-primary sd-btn-glow" onClick={handleSignup} style={{ width: "100%" }}>
                  <span>Create Account</span><i className="fas fa-rocket"></i>
                </button>
                <p className="sd-switch-auth">Already have an account?{" "}
                  <a href="#!" onClick={e => { e.preventDefault(); setAuthMode("login"); setAuthError(""); }}>Sign In</a>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== DASHBOARD (after login) ========== */}
      {!authLoading && currentUser && (
        <div className="sd-screen sd-active sd-dashboard">
          {/* Top Nav */}
          <nav className="sd-top-nav sd-glass-nav">
            <div className="sd-nav-left sd-nav-left-cluster">
              <div className="sd-logo-small"><i className="fas fa-chart-line"></i> Sales<span className="sd-accent">Dash</span></div>
              <Link to="/chorevault" className="sd-eco-link" title="Family chores on VMCREATIONS">
                <i className="fas fa-vault"></i><span className="sd-eco-link-text"> ChoreVault</span>
              </Link>
            </div>
            <div className="sd-nav-center">
              {tabs.map(t => (
                <button key={t.key} className={`sd-nav-tab ${tab === t.key ? "sd-active" : ""}`} onClick={() => setTab(t.key)}>
                  <i className={`fas ${t.icon}`}></i><span className="sd-tab-label">{t.label}</span>
                </button>
              ))}
            </div>
            <div className="sd-nav-right">
              {lowStock.length > 0 && (
                <div className="sd-stock-alert" title={`${lowStock.length} product(s) low on stock`}>
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{lowStock.length}</span>
                </div>
              )}
              <div className="sd-user-avatar" onClick={e => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}>
                <i className="fas fa-user-tie"></i>
              </div>
              {showUserMenu && (
                <div className="sd-user-menu sd-glass-card" onClick={(e) => e.stopPropagation()}>
                  <p className="sd-menu-name">{userProfile?.name || currentUser.displayName || "User"}</p>
                  <p className="sd-menu-email">{currentUser.email}</p>
                  <p className="sd-menu-biz"><i className="fas fa-building"></i> {userProfile?.businessName || "My Business"}</p>
                  <hr />
                  <p className="sd-menu-section-label">Export Data</p>
                  <button type="button" className="sd-menu-action" onClick={handleExportBackupZip} disabled={backupBusy}>
                    <i className="fas fa-file-zipper"></i> {backupBusy ? "Creating ZIP..." : "Full backup ZIP"}
                  </button>
                  <button type="button" className="sd-menu-action" onClick={handleExportAllSalesCsv}>
                    <i className="fas fa-file-csv"></i> All sales CSV
                  </button>
                  <button type="button" className="sd-menu-action" onClick={handleExportProductsCsv}>
                    <i className="fas fa-box"></i> Products CSV
                  </button>
                  <button type="button" className="sd-menu-action" onClick={handleExportCustomersCsv}>
                    <i className="fas fa-users"></i> Customers CSV
                  </button>
                  <hr />
                  <button
                    type="button"
                    className="sd-menu-action sd-menu-danger"
                    onClick={() => {
                      setShowDeleteModal(true);
                      setDeleteError("");
                      setDeletePassword("");
                      setDeleteConfirmText("");
                      setShowUserMenu(false);
                    }}
                  >
                    <i className="fas fa-user-slash"></i> Delete account…
                  </button>
                  <hr />
                  <button type="button" className="sd-menu-action sd-menu-danger" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i> Log out
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Firestore Error Banner */}
          {firestoreError && (
            <div style={{ background: "rgba(255,45,45,0.15)", border: "1px solid rgba(255,45,45,0.5)", color: "#ff6b6b", padding: "12px 20px", margin: "70px 20px 0", borderRadius: 8, fontFamily: "monospace", fontSize: 14 }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
              <strong>Firestore Error:</strong> {firestoreError}
            </div>
          )}

          {/* Tab Content */}
          <div className="sd-tab-content">

            {/* ===== DASHBOARD TAB ===== */}
            {tab === "dashboard" && (
              <div className="sd-section">
                <div className="sd-dashboard-header">
                  <div>
                    <h1>Welcome back, {(userProfile?.name || currentUser.displayName || "User").split(" ")[0]} 👋</h1>
                    <p className="sd-subtitle">Here's your business at a glance — same site as ChoreVault and your other projects.</p>
                  </div>
                </div>

                <div className="sd-card sd-glass-card sd-goal-card">
                  <div className="sd-goal-card-head">
                    <h3><i className="fas fa-bullseye"></i> Sales goal</h3>
                    <p className="sd-goal-hint">Pick a product and how many units to sell (e.g. 500 pizzas). Only sales of that product with “count toward goal” on fill the bar.</p>
                  </div>
                  {Number(userProfile?.salesGoalTarget) > 0 && !userProfile?.salesGoalProductId && (
                    <p className="sd-info-box sd-goal-migrate-hint">
                      You had a dollar-based goal saved before. Set a product and unit target below — progress is now tracked in units sold, not dollars.
                    </p>
                  )}
                  <div className="sd-form sd-goal-form" role="group" aria-label="Sales goal">
                    <div className="sd-form-row">
                      <div className="sd-form-group">
                        <label>Product</label>
                        <select
                          value={goalFormProductId}
                          onChange={(e) => setGoalFormProductId(e.target.value)}
                          disabled={userProducts.length === 0}
                          autoComplete="off"
                        >
                          <option value="">— Select product —</option>
                          {userProducts.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="sd-form-group">
                        <label>Target quantity</label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={goalFormQty}
                          onChange={(e) => setGoalFormQty(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSaveSalesGoal(e);
                            }
                          }}
                          placeholder="e.g. 500"
                          autoComplete="off"
                        />
                      </div>
                      <div className="sd-form-group">
                        <label>Label <span className="sd-muted">(optional)</span></label>
                        <input
                          type="text"
                          value={goalFormLabel}
                          onChange={(e) => setGoalFormLabel(e.target.value)}
                          placeholder="e.g. Spring promo"
                          maxLength={120}
                          autoComplete="off"
                        />
                      </div>
                      <div className="sd-form-group sd-goal-save-wrap">
                        <label className="sd-label-spacer">&nbsp;</label>
                        <button type="button" className="sd-btn sd-btn-secondary" onClick={handleSaveSalesGoal}>
                          Save goal
                        </button>
                      </div>
                    </div>
                    {goalSaveMsg && <div className="sd-success-msg sd-goal-msg"><i className="fas fa-check-circle"></i> {goalSaveMsg}</div>}
                  </div>
                  {goalProductId && goalQtyTarget > 0 ? (
                    <div className="sd-goal-progress-block">
                      <div className="sd-goal-progress-labels">
                        <span className="sd-goal-name">
                          {goalLabelDisplay || goalProduct?.name || "Your goal"}
                        </span>
                        <span className="sd-goal-numbers">
                          <strong className="sd-cyan">{soldUnitsTowardGoal}</strong>
                          {" / "}
                          <strong>{goalQtyTarget}</strong>
                          {" "}
                          <span className="sd-muted">
                            {goalProduct?.name ? `${goalProduct.name} sold` : "units"}
                            {" "}({goalProgressPct.toFixed(0)}%)
                          </span>
                        </span>
                      </div>
                      <div className="sd-goal-track" role="progressbar" aria-valuenow={Math.round(goalProgressPct)} aria-valuemin={0} aria-valuemax={100} aria-label="Progress toward sales goal">
                        <div className="sd-goal-fill" style={{ width: `${goalProgressPct}%` }} />
                      </div>
                    </div>
                  ) : (
                    <p className="sd-empty sd-goal-empty">Choose a product and target quantity above to track progress.</p>
                  )}
                </div>

                <div className="sd-stats-grid">
                  <StatCard icon={<i className="fas fa-dollar-sign"></i>} title="Total Revenue" value={formatCurrency(totalRevenue)} borderClass="sd-neon-cyan" />
                  <StatCard icon={<i className="fas fa-receipt"></i>} title="Total Sales" value={totalSalesCount} borderClass="sd-neon-green" />
                  <StatCard icon={<i className="fas fa-boxes-stacked"></i>} title="Products" value={totalProductsCount} borderClass="sd-neon-purple" />
                  <StatCard icon={<i className="fas fa-users"></i>} title="Customers" value={totalCustomersCount} borderClass="sd-neon-pink" />
                </div>

                <div className="sd-charts-row">
                  <div className="sd-card sd-glass-card">
                    <h3><i className="fas fa-chart-bar"></i> Revenue Last 7 Days</h3>
                    <BarChart data={last7} height={140} />
                  </div>
                  <div className="sd-card sd-glass-card">
                    <h3><i className="fas fa-trophy"></i> Top Products by Revenue</h3>
                    <BarChart data={revenueByProduct} height={140} />
                  </div>
                </div>

                <div className="sd-charts-row">
                  <div className="sd-card sd-glass-card" style={{ flex: 1 }}>
                    <h3><i className="fas fa-user-group"></i> Top Customers</h3>
                    {topCustomers.length === 0 ? (
                      <p className="sd-empty">No customers yet.</p>
                    ) : (
                      <table className="sd-table">
                        <thead><tr><th>Customer</th><th>Sales</th><th>Total Spent</th></tr></thead>
                        <tbody>
                          {topCustomers.map((c, i) => (
                            <tr key={i}>
                              <td><span className="sd-badge sd-badge-cyan">{c.name}</span></td>
                              <td>{c.salesCount}</td>
                              <td><strong>{formatCurrency(c.totalSpent)}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div className="sd-card sd-glass-card" style={{ flex: 1 }}>
                    <h3><i className="fas fa-exclamation-triangle"></i> Low Stock Alerts</h3>
                    {lowStock.length === 0 ? (
                      <p className="sd-empty">All products well stocked ✅</p>
                    ) : (
                      <table className="sd-table">
                        <thead><tr><th>Product</th><th>Stock</th></tr></thead>
                        <tbody>
                          {lowStock.map(p => (
                            <tr key={p.id}>
                              <td>{p.name}</td>
                              <td><span className="sd-badge sd-badge-red">{p.stock} left</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Recent Sales */}
                <div className="sd-card sd-glass-card">
                  <h3><i className="fas fa-clock-rotate-left"></i> Recent Sales</h3>
                  {userSales.length === 0 ? (
                    <p className="sd-empty">No sales recorded yet. Go to "Record Sale" to add your first sale!</p>
                  ) : (
                    <table className="sd-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Product</th>
                          <th>Occasion</th>
                          <th>Qty</th>
                          <th>Customer</th>
                          <th>Goal</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...userSales].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8).map(s => (
                          <tr key={s.id}>
                            <td>{formatDate(s.date)}</td>
                            <td>{s.productName}</td>
                            <td className="sd-muted">{s.occasion || "—"}</td>
                            <td>{s.qty}</td>
                            <td>{s.customer}</td>
                            <td>{s.countsTowardGoal === false ? <span className="sd-badge sd-badge-muted" title="Not counted">Off</span> : <span className="sd-badge sd-badge-green">On</span>}</td>
                            <td><strong className="sd-green">{formatCurrency(s.total)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* ===== RECORD SALE TAB ===== */}
            {tab === "record-sale" && (
              <div className="sd-section">
                <div className="sd-card sd-glass-card sd-form-card">
                  <h3><i className="fas fa-cash-register"></i> Record a New Sale</h3>
                  {userProducts.length === 0 ? (
                    <div className="sd-info-box">No products found. Please add products first in the <button className="sd-link-btn" onClick={() => setTab("products")}>Products</button> tab.</div>
                  ) : (
                    <form onSubmit={handleAddSale} className="sd-form">
                      <div className="sd-form-row">
                        <div className="sd-form-group">
                          <label>Product *</label>
                          <select value={saleProductId} onChange={e => setSaleProductId(e.target.value)} required>
                            <option value="">Select a product…</option>
                            {userProducts.map(p => (
                              <option key={p.id} value={p.id}>{p.name} — {formatCurrency(p.price)} (stock: {p.stock})</option>
                            ))}
                          </select>
                        </div>
                        <div className="sd-form-group">
                          <label>Quantity *</label>
                          <input type="number" min="1" value={saleQty} onChange={e => setSaleQty(e.target.value)} placeholder="e.g. 3" required />
                        </div>
                      </div>
                      <div className="sd-form-row">
                        <div className="sd-form-group">
                          <label>Customer Name</label>
                          <input type="text" value={saleCustomer} onChange={e => setSaleCustomer(e.target.value)} placeholder="Walk-in customer" list="customer-suggestions" />
                          <datalist id="customer-suggestions">
                            {userCustomers.map(c => <option key={c.id} value={c.name} />)}
                          </datalist>
                        </div>
                        <div className="sd-form-group">
                          <label>Sale Date *</label>
                          <input type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)} required />
                        </div>
                      </div>
                      <div className="sd-form-group">
                        <label>Why / occasion <span className="sd-label-optional">(optional)</span></label>
                        <input
                          type="text"
                          value={saleOccasion}
                          onChange={(e) => setSaleOccasion(e.target.value)}
                          placeholder="e.g. Scout bake sale, birthday party booth…"
                          maxLength={160}
                        />
                      </div>
                      <div className="sd-form-group">
                        <label>Note / Payment Method</label>
                        <input type="text" value={saleNote} onChange={e => setSaleNote(e.target.value)} placeholder="e.g. Paid by card, online order…" />
                      </div>
                      <label className="sd-checkbox-row">
                        <input
                          type="checkbox"
                          checked={saleCountsTowardGoal}
                          onChange={(e) => setSaleCountsTowardGoal(e.target.checked)}
                        />
                        <span>Count this sale toward my sales goal</span>
                      </label>
                      {saleProductId && (() => {
                        const selProduct = userProducts.find(p => p.id === saleProductId);
                        const selStock = selProduct?.stock || 0;
                        const selQty = parseInt(saleQty) || 0;
                        return (
                          <>
                            <div className="sd-total-preview">
                              Available Stock: <strong>{selStock}</strong>
                              {saleQty && <> — Estimated Total: <strong>{formatCurrency((selProduct?.price || 0) * selQty)}</strong></>}
                            </div>
                            {selQty > selStock && selQty > 0 && (
                              <div className="sd-auth-error" style={{ marginTop: 4 }}>
                                <i className="fas fa-exclamation-circle"></i> Quantity exceeds available stock ({selStock})
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {saleMsg && <div className="sd-success-msg"><i className="fas fa-check-circle"></i> {saleMsg}</div>}
                      <button type="submit" className="sd-btn sd-btn-primary">Record Sale</button>
                    </form>
                  )}
                </div>
              </div>
            )}

            {/* ===== PRODUCTS TAB ===== */}
            {tab === "products" && (
              <div className="sd-section">
                <div className="sd-card sd-glass-card sd-form-card">
                  <h3>{prodEditId ? <><i className="fas fa-pen"></i> Edit Product</> : <><i className="fas fa-plus"></i> Add New Product</>}</h3>
                  <form onSubmit={handleAddProduct} className="sd-form">
                    <div className="sd-form-row">
                      <div className="sd-form-group">
                        <label>Product Name *</label>
                        <input type="text" value={prodName} onChange={e => setProdName(e.target.value)} placeholder="e.g. Blue T-Shirt" required />
                      </div>
                      <div className="sd-form-group">
                        <label>Price (USD) *</label>
                        <input type="number" min="0" step="0.01" value={prodPrice} onChange={e => setProdPrice(e.target.value)} placeholder="e.g. 29.99" required />
                      </div>
                    </div>
                    <div className="sd-form-row">
                      <div className="sd-form-group">
                        <label>Category</label>
                        <select value={prodCategory} onChange={e => setProdCategory(e.target.value)}>
                          {["General", "Electronics", "Clothing", "Food & Beverages", "Software", "Services", "Health & Beauty", "Home & Garden", "Sports", "Books", "Other"].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="sd-form-group">
                        <label>Stock Quantity</label>
                        <input type="number" min="0" value={prodStock} onChange={e => setProdStock(e.target.value)} placeholder="e.g. 100" />
                      </div>
                    </div>
                    {prodMsg && <div className="sd-success-msg"><i className="fas fa-check-circle"></i> {prodMsg}</div>}
                    <div style={{ display: "flex", gap: 12 }}>
                      <button type="submit" className="sd-btn sd-btn-primary">{prodEditId ? "Update Product" : "Add Product"}</button>
                      {prodEditId && <button type="button" className="sd-btn sd-btn-secondary" onClick={() => { setProdEditId(null); setProdName(""); setProdPrice(""); setProdCategory("General"); setProdStock(""); }}>Cancel</button>}
                    </div>
                  </form>
                </div>

                <div className="sd-card sd-glass-card">
                  <h3><i className="fas fa-boxes-stacked"></i> All Products ({userProducts.length})</h3>
                  {userProducts.length === 0 ? (
                    <p className="sd-empty">No products yet. Add your first product above.</p>
                  ) : (
                    <table className="sd-table">
                      <thead>
                        <tr><th>Name</th><th>Category</th><th>Price</th><th>Stock</th><th>Revenue</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {userProducts.map(p => {
                          const rev = userSales.filter(s => s.productId === p.id).reduce((sum, s) => sum + s.total, 0);
                          return (
                            <tr key={p.id}>
                              <td><strong>{p.name}</strong></td>
                              <td><span className="sd-badge sd-badge-muted">{p.category}</span></td>
                              <td>{formatCurrency(p.price)}</td>
                              <td>
                                <span className={`sd-badge ${(p.stock || 0) <= 5 ? "sd-badge-red" : "sd-badge-green"}`}>
                                  {p.stock || 0}
                                </span>
                              </td>
                              <td><strong>{formatCurrency(rev)}</strong></td>
                              <td>
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button className="sd-btn-small sd-btn-cyan" onClick={() => handleEditProduct(p)}>Edit</button>
                                  <button className="sd-btn-small sd-btn-danger" onClick={() => handleDeleteProduct(p.id)}>Delete</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* ===== SALES HISTORY TAB ===== */}
            {tab === "sales-history" && (
              <div className="sd-section">
                <div className="sd-card sd-glass-card">
                  <h3><i className="fas fa-filter"></i> Filter Sales</h3>
                  <div className="sd-form-row">
                    <div className="sd-form-group">
                      <label>Search by product, customer, or occasion</label>
                      <input type="text" value={salesSearch} onChange={e => setSalesSearch(e.target.value)} placeholder="Search…" />
                    </div>
                    <div className="sd-form-group">
                      <label>From date</label>
                      <input type="date" value={salesDateFrom} onChange={e => setSalesDateFrom(e.target.value)} />
                    </div>
                    <div className="sd-form-group">
                      <label>To date</label>
                      <input type="date" value={salesDateTo} onChange={e => setSalesDateTo(e.target.value)} />
                    </div>
                  </div>
                  <div className="sd-filter-summary sd-filter-actions">
                    <span>
                      Showing <strong>{filteredSales.length}</strong> of <strong>{userSales.length}</strong> sales
                      {filteredSales.length > 0 && <> — Total: <strong className="sd-cyan">{formatCurrency(filteredSales.reduce((s, r) => s + r.total, 0))}</strong></>}
                    </span>
                    {filteredSales.length > 0 && (
                      <button type="button" className="sd-btn sd-btn-secondary sd-btn-sm" onClick={handleExportFilteredSalesCsv}>
                        <i className="fas fa-download"></i> Download filtered CSV
                      </button>
                    )}
                  </div>
                </div>

                <div className="sd-card sd-glass-card">
                  <h3><i className="fas fa-receipt"></i> Sales History</h3>
                  {filteredSales.length === 0 ? (
                    <p className="sd-empty">No sales match your filters.</p>
                  ) : (
                    <table className="sd-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Product</th>
                          <th>Occasion</th>
                          <th>Unit Price</th>
                          <th>Qty</th>
                          <th>Customer</th>
                          <th>Goal</th>
                          <th>Total</th>
                          <th>Note</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSales.map(s => (
                          <tr key={s.id}>
                            <td style={{ whiteSpace: "nowrap" }}>{formatDate(s.date)}</td>
                            <td>{s.productName}</td>
                            <td className="sd-muted">{s.occasion || "—"}</td>
                            <td>{formatCurrency(s.productPrice)}</td>
                            <td>{s.qty}</td>
                            <td>{s.customer}</td>
                            <td>{s.countsTowardGoal === false ? <span className="sd-badge sd-badge-muted">Off</span> : <span className="sd-badge sd-badge-green">On</span>}</td>
                            <td><strong className="sd-green">{formatCurrency(s.total)}</strong></td>
                            <td className="sd-muted">{s.note || "—"}</td>
                            <td><button className="sd-btn-small sd-btn-danger" onClick={() => handleDeleteSale(s.id)}>Delete</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* ===== CUSTOMERS TAB ===== */}
            {tab === "customers" && (
              <div className="sd-section">
                <div className="sd-card sd-glass-card sd-form-card">
                  <h3><i className="fas fa-user-plus"></i> Add New Customer</h3>
                  <form onSubmit={handleAddCustomer} className="sd-form">
                    <div className="sd-form-row">
                      <div className="sd-form-group">
                        <label>Full Name *</label>
                        <input type="text" value={custName} onChange={e => setCustName(e.target.value)} placeholder="e.g. John Smith" required />
                      </div>
                      <div className="sd-form-group">
                        <label>Email</label>
                        <input type="email" value={custEmail} onChange={e => setCustEmail(e.target.value)} placeholder="john@example.com" />
                      </div>
                      <div className="sd-form-group">
                        <label>Phone</label>
                        <input type="tel" value={custPhone} onChange={e => setCustPhone(e.target.value)} placeholder="+1 555 000 0000" />
                      </div>
                    </div>
                    {custMsg && <div className="sd-success-msg"><i className="fas fa-check-circle"></i> {custMsg}</div>}
                    <button type="submit" className="sd-btn sd-btn-primary">Add Customer</button>
                  </form>
                </div>

                <div className="sd-card sd-glass-card">
                  <h3><i className="fas fa-users"></i> All Customers ({userCustomers.length})</h3>
                  {userCustomers.length === 0 ? (
                    <p className="sd-empty">No customers yet. They'll be added automatically when you record a sale, or add them manually above.</p>
                  ) : (
                    <table className="sd-table">
                      <thead>
                        <tr><th>Name</th><th>Email</th><th>Phone</th><th>Sales</th><th>Total Spent</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {userCustomers.map(c => {
                          const custSales = userSales.filter(s => s.customer.toLowerCase() === c.name.toLowerCase());
                          const spent = custSales.reduce((sum, s) => sum + s.total, 0);
                          return (
                            <tr key={c.id}>
                              <td><strong>{c.name}</strong></td>
                              <td className="sd-muted">{c.email || "—"}</td>
                              <td className="sd-muted">{c.phone || "—"}</td>
                              <td>{custSales.length}</td>
                              <td><strong className="sd-cyan">{formatCurrency(spent)}</strong></td>
                              <td><button className="sd-btn-small sd-btn-danger" onClick={() => handleDeleteCustomer(c.id)}>Delete</button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

          </div>

          {showGoalCelebration && (
            <div
              className="sd-modal-overlay sd-goal-celebration-overlay"
              role="presentation"
              aria-hidden={!showGoalCelebration}
            >
              <div
                className="sd-goal-celebration sd-glass-card"
                role="alertdialog"
                aria-live="assertive"
                aria-label="Goal achieved"
              >
                <div className="sd-goal-celebration-confetti" aria-hidden="true" />
                <div className="sd-goal-celebration-icon" aria-hidden="true">🎉</div>
                <h3 className="sd-goal-celebration-title">Goal achieved!</h3>
                <p className="sd-goal-celebration-body">
                  You reached{" "}
                  <strong>
                    {goalQtyTarget} {goalProduct?.name ? goalProduct.name : "units"}
                  </strong>
                  {goalLabelDisplay ? (
                    <> — <span className="sd-muted">{goalLabelDisplay}</span></>
                  ) : null}
                </p>
                <p className="sd-goal-celebration-sub sd-muted">Nice work — keep the momentum going.</p>
              </div>
            </div>
          )}

          {showDeleteModal && (
            <div
              className="sd-modal-overlay"
              role="presentation"
              onClick={(e) => {
                if (deleteBusy) return;
                if (e.target === e.currentTarget) {
                  setShowDeleteModal(false);
                  setDeleteError("");
                }
              }}
            >
              <div className="sd-delete-modal sd-glass-card" role="dialog" aria-labelledby="sd-delete-title" aria-modal="true">
                <h3 id="sd-delete-title"><i className="fas fa-triangle-exclamation"></i> Delete account</h3>
                <p className="sd-delete-warning">
                  This permanently deletes your profile, products, sales, and customers from this app. You cannot undo it.
                </p>
                <p className="sd-delete-hint">Type <strong>DELETE</strong> to confirm, then enter your password (email sign-in only).</p>
                <div className="sd-form-group">
                  <label>Confirmation</label>
                  <input
                    type="text"
                    autoComplete="off"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE"
                    disabled={deleteBusy}
                  />
                </div>
                <div className="sd-form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Your account password"
                    disabled={deleteBusy}
                  />
                </div>
                {deleteError && (
                  <div className="sd-auth-error" style={{ marginBottom: 12 }}>
                    <i className="fas fa-exclamation-circle"></i> {deleteError}
                  </div>
                )}
                <div className="sd-delete-actions">
                  <button
                    type="button"
                    className="sd-btn sd-btn-secondary"
                    disabled={deleteBusy}
                    onClick={() => {
                      if (!deleteBusy) {
                        setShowDeleteModal(false);
                        setDeleteError("");
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sd-btn sd-btn-danger-solid"
                    disabled={deleteBusy}
                    onClick={handleDeleteAccount}
                  >
                    {deleteBusy ? "Working…" : "Delete forever"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
